/* eslint-disable @typescript-eslint/no-explicit-any */
import Payments from "../services/Payments";
import ERROR_MESSAGES from "common/constants/ErrorMessages";

interface StripeError {
    error?: {errorMessage: string};
}

interface StripeToken {
    id: string;
}

interface StripeTokenWrapper extends StripeError {
    token?: StripeToken;
}

interface StripeSource extends StripeError {
    source?: {
        card: {
            id?: string;
            three_d_secure?: string; // eslint-disable-line camelcase
        };
    };
}

interface Stripe {
    createToken: () => Promise<StripeTokenWrapper>;
    createSource: () => Promise<StripeSource>;
}

interface Token {
    card: {id: string};
    id: string;
}

interface StripeGetCustomerResolveElement {
    id: string;
}

function addCardToStripe(
    token: Token,
    email: string,
    grecaptchaToken: string | null | undefined,
    stripeApiTrackingName: string,
    cardInfo: {postalCode?: string},
    skipGrecaptcha?: string): Promise<any> {
    const cardId = token.card.id;
    const tokenId = token.id;

    return Payments.addCustomerToStripe(tokenId, email, grecaptchaToken, stripeApiTrackingName, skipGrecaptcha)
        .then(({ id: customerId }) => Payments.addCardMetadataToStripe(cardId, customerId, cardInfo));
}

function getCustomerFromStripe(email: string): Promise<string | null> {
    return Payments.getCustomerFromStripe(email)
        .then(({ data }: {data: StripeGetCustomerResolveElement[]}) => {
            return data.length ? data[0].id : null;
        });
}

function getStripeToken(stripe: Stripe): Promise<StripeToken> {
    return getStripeSource(stripe)
        .then(({ source, token }) => {
            if (source === undefined
                || source.card.three_d_secure === undefined
                || source.card.three_d_secure === "required") {
                return Promise.reject({ errorMessage: ERROR_MESSAGES.THREE_D_SECURE });
            }

            return token as StripeToken;
        });
}

function getStripeSource(stripe: Stripe): Promise<StripeSource & StripeTokenWrapper> {
    return stripe.createToken().then(({ error: tokenError, token }) => {
        if (tokenError) {
            return Promise.reject(tokenError);
        }

        return stripe.createSource().then(({ error: sourceError, source }) => {
            if (sourceError) {
                return Promise.reject(sourceError);
            }

            return Promise.resolve({ source, token });
        });
    });
}

export default {
    addCardToStripe,
    getCustomerFromStripe,
    getStripeToken,
    getStripeSource
};
